import React, { useEffect, useRef, useState } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { useContactModal } from "../context/ContactModalContext";
import { categories, findProduct } from "../data/products";

// Featured product picks shown in the Products mega-menu (10 total)
const FEATURED_SLUGS = {
  vegetables: ["onion", "potato", "green-chilli"],
  fruits: ["banana", "mango", "pomegranate"],
  spices: ["turmeric", "black-pepper", "cardamom"],
  processed: ["raisins"],
};

const FEATURED_BY_CATEGORY = categories
  .map((c) => ({
    ...c,
    items: (FEATURED_SLUGS[c.key] || [])
      .map((slug) => findProduct(c.key, slug)?.item)
      .filter(Boolean),
  }))
  .filter((c) => c.items.length > 0);

const links = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/products", label: "Products", hasMenu: true },
  { to: "/why-us", label: "Why Us" },
  { to: "/global-reach", label: "Global Reach" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [productsOpen, setProductsOpen] = useState(false);
  const [mobileProductsOpen, setMobileProductsOpen] = useState(false);
  const closeTimer = useRef(null);
  const location = useLocation();
  const { openModal } = useContactModal();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mega menu when route changes
  useEffect(() => {
    setProductsOpen(false);
    setOpen(false);
    setMobileProductsOpen(false);
  }, [location.pathname]);

  const openProducts = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setProductsOpen(true);
  };
  const scheduleClose = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => setProductsOpen(false), 150);
  };

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 w-full transition-all duration-500 ${scrolled ? "bg-[#F5F1E8]/75 backdrop-blur-2xl backdrop-saturate-150 border-b border-stone-900/8 shadow-[0_4px_30px_rgba(0,0,0,0.08)]" : "bg-gradient-to-b from-transparent via-transparent to-transparent"}`}
    >
      <div className="w-full px-4 sm:px-6 lg:px-10">
        <nav className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}

          <img
            src="/myriad-energy-logo.png"
            alt="Myriad Green Energy"
            className="w-[195px] h-[175px] md:w-[195px] md:h-[175px] p-1 mt-[-24px] cursor-pointer"
            onClick={() => {
              navigate("/");
            }}
          />

          {/* Desktop links */}
          <ul className="hidden lg:flex items-center gap-1">
            {links.map((l) => {
              if (l.hasMenu) {
                return (
                  <li
                    key={l.to}
                    className="relative"
                    onMouseEnter={openProducts}
                    onMouseLeave={scheduleClose}
                  >
                    <NavLink
                      to={l.to}
                      end={l.end}
                      aria-haspopup="true"
                      aria-expanded={productsOpen}
                      className={({ isActive }) =>
                        `relative inline-flex items-center gap-1 px-4 py-2 rounded-xl text-sm transition ${isActive || productsOpen ? "text-stone-900 bg-stone-900/10" : "text-stone-600 hover:text-stone-900 hover:bg-stone-900/6"}`
                      }
                    >
                      {({ isActive }) => (
                        <>
                          {l.label}
                          <svg
                            viewBox="0 0 24 24"
                            className={`w-3.5 h-3.5 transition-transform ${productsOpen ? "rotate-180" : ""}`}
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              d="M19 9l-7 7-7-7"
                            />
                          </svg>
                          {isActive && (
                            <span className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-amber-300 shadow-[0_0_10px_rgba(251,191,36,0.8)]" />
                          )}
                        </>
                      )}
                    </NavLink>

                    {/* MEGA MENU PANEL */}
                    {productsOpen && (
                      <div
                        onMouseEnter={openProducts}
                        onMouseLeave={scheduleClose}
                        className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-screen max-w-5xl"
                      >
                        <div className="rounded-2xl bg-white border border-stone-900/10 shadow-2xl shadow-stone-900/15 overflow-hidden fade-up">
                          <div className="grid grid-cols-4 gap-0 divide-x divide-stone-900/8">
                            {FEATURED_BY_CATEGORY.map((c) => (
                              <div key={c.key} className="p-5">
                                <Link
                                  to={`/products/${c.key}`}
                                  className="flex items-center justify-between gap-2 mb-3 group/cat"
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-base">{c.emoji}</span>
                                    <span className="text-[11px] uppercase tracking-[0.18em] text-stone-500 font-semibold group-hover/cat:text-emerald-700 transition">
                                      {c.label}
                                    </span>
                                  </div>
                                  <svg
                                    viewBox="0 0 24 24"
                                    className="w-3 h-3 text-stone-400 group-hover/cat:text-emerald-700 transition"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="2"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      d="M9 5l7 7-7 7"
                                    />
                                  </svg>
                                </Link>

                                <ul className="space-y-1">
                                  {c.items.map((item) => (
                                    <li key={item.slug}>
                                      <Link
                                        to={`/products/${c.key}/${item.slug}`}
                                        className="flex items-center gap-3 px-2 py-2 -mx-2 rounded-lg hover:bg-emerald-50/60 transition group/item"
                                      >
                                        <div
                                          className={`shrink-0 w-9 h-9 rounded-lg bg-linear-to-br ${c.accent} flex items-center justify-center text-base shadow-sm`}
                                        >
                                          {item.emoji}
                                        </div>
                                        <div className="min-w-0">
                                          <div className="text-sm font-medium text-stone-900 truncate group-hover/item:text-emerald-800">
                                            {item.name}
                                          </div>
                                          <div className="text-[11px] text-stone-500 truncate">
                                            {item.season}
                                          </div>
                                        </div>
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            ))}
                          </div>

                          {/* Footer band */}
                          <div className="px-5 py-3.5 bg-stone-50/60 border-t border-stone-900/8 flex items-center justify-between">
                            <span className="text-xs text-stone-500">
                              Browse the full catalog · 100+ SKUs across 4
                              product families
                            </span>
                            <Link
                              to="/products"
                              className="inline-flex items-center gap-1.5 text-xs font-medium text-emerald-700 hover:text-emerald-800 transition"
                            >
                              View all products
                              <svg
                                viewBox="0 0 24 24"
                                className="w-3.5 h-3.5"
                                fill="none"
                                stroke="currentColor"
                                strokeWidth="2"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  d="M5 12h14M13 5l7 7-7 7"
                                />
                              </svg>
                            </Link>
                          </div>
                        </div>
                      </div>
                    )}
                  </li>
                );
              }

              return (
                <li key={l.to}>
                  <NavLink
                    to={l.to}
                    end={l.end}
                    className={({ isActive }) =>
                      `relative px-4 py-2 rounded-xl text-sm transition ${isActive ? "text-stone-900 bg-stone-900/10" : "text-stone-600 hover:text-stone-900 hover:bg-stone-900/6"}`
                    }
                  >
                    {({ isActive }) => (
                      <>
                        {l.label}
                        {isActive && (
                          <span className="absolute left-1/2 -bottom-1 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-amber-300 shadow-[0_0_10px_rgba(251,191,36,0.8)]" />
                        )}
                      </>
                    )}
                  </NavLink>
                </li>
              );
            })}
          </ul>

          {/* CTA */}
          <div className="hidden md:flex items-center gap-3">
            <button
              onClick={openModal}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-medium text-white bg-linear-to-br from-emerald-700 via-emerald-600 to-teal-700 hover:from-emerald-600 hover:to-emerald-700 transition shadow-lg shadow-emerald-600/25"
            >
              Request Quote
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                className="w-4 h-4"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 12h14M13 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          {/* Hamburger */}
          <button
            aria-label="Toggle menu"
            onClick={() => setOpen((o) => !o)}
            className="lg:hidden w-11 h-11 rounded-xl glass flex items-center justify-center text-stone-900 cursor-pointer"
          >
            <svg
              viewBox="0 0 24 24"
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              {open ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M6 6l12 12M6 18L18 6"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </nav>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="lg:hidden border-t border-stone-900/8 bg-[#F5F1E8]/95 backdrop-blur-2xl fade-up max-h-[calc(100vh-4rem)] overflow-y-auto">
          <div className="px-4 sm:px-6 py-4 flex flex-col gap-1">
            {links.map((l) => {
              if (l.hasMenu) {
                return (
                  <div key={l.to}>
                    <div className="flex items-center">
                      <NavLink
                        to={l.to}
                        end={l.end}
                        onClick={() => setOpen(false)}
                        className={({ isActive }) =>
                          `flex-1 px-4 py-3 rounded-xl text-sm ${isActive ? "bg-stone-900/10 text-stone-900" : "text-stone-700 hover:bg-stone-900/6"}`
                        }
                      >
                        {l.label}
                      </NavLink>
                      <button
                        onClick={() => setMobileProductsOpen((v) => !v)}
                        aria-label="Toggle product menu"
                        className="w-11 h-11 flex items-center justify-center text-stone-600"
                      >
                        <svg
                          viewBox="0 0 24 24"
                          className={`w-4 h-4 transition-transform ${mobileProductsOpen ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                    </div>

                    {mobileProductsOpen && (
                      <div className="ml-3 mt-1 mb-2 pl-3 border-l-2 border-emerald-600/20 space-y-3 fade-up">
                        {FEATURED_BY_CATEGORY.map((c) => (
                          <div key={c.key}>
                            <Link
                              to={`/products/${c.key}`}
                              onClick={() => setOpen(false)}
                              className="flex items-center gap-2 px-2 py-1.5 text-[11px] uppercase tracking-[0.18em] text-stone-500 font-semibold hover:text-emerald-700"
                            >
                              <span>{c.emoji}</span>
                              {c.label}
                            </Link>
                            <ul className="mt-0.5">
                              {c.items.map((item) => (
                                <li key={item.slug}>
                                  <Link
                                    to={`/products/${c.key}/${item.slug}`}
                                    onClick={() => setOpen(false)}
                                    className="flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm text-stone-700 hover:bg-emerald-50/60 hover:text-emerald-800"
                                  >
                                    <span className="text-base">
                                      {item.emoji}
                                    </span>
                                    {item.name}
                                  </Link>
                                </li>
                              ))}
                            </ul>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              }
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  onClick={() => setOpen(false)}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-xl text-sm ${isActive ? "bg-stone-900/10 text-stone-900" : "text-stone-700 hover:bg-stone-900/6"}`
                  }
                >
                  {l.label}
                </NavLink>
              );
            })}
            <button
              onClick={() => {
                setOpen(false);
                openModal();
              }}
              className="mt-1 px-4 py-3 rounded-xl text-sm font-medium text-white bg-linear-to-br from-emerald-700 to-teal-700 text-center w-full"
            >
              Request Quote
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
